import React, { useState } from "react";
import { Button } from "./ui/button";
import { ShieldCheck, CheckCircle2, FileText, Camera, UserCircle } from "lucide-react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { motion } from "framer-motion";
import VpnWarning from "./VpnWarning";

export default function KycScreen({ onSkip, onComplete }) {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [processing, setProcessing] = useState(false);

  if (user?.kyc_tier >= 1) {
    return (
      <div className="text-center py-10 space-y-4" data-testid="kyc-already-done">
        <div className="w-16 h-16 mx-auto rounded-full bg-accent/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-accent" />
        </div>
        <div>
          <h3 className="font-display text-xl font-bold text-primary">Identity verified</h3>
          <p className="text-sm text-slate-500 mt-1">You can transact up to €5,000 per day.</p>
        </div>
        <Button onClick={onComplete} className="bg-accent hover:bg-accent/90 text-white rounded-xl h-12 px-8 font-display font-semibold" data-testid="kyc-continue-btn">
          Continue
        </Button>
      </div>
    );
  }

  const steps = [
    { icon: FileText, title: "Country & document", desc: "Cyprus passport / ID" },
    { icon: Camera, title: "Capture document", desc: "Front & back, well-lit" },
    { icon: UserCircle, title: "Selfie + liveness", desc: "Look at the camera" },
  ];

  const run = async () => {
    setProcessing(true);
    await api.post("/kyc/start");
    setTimeout(async () => {
      const r = await api.post("/kyc/complete");
      setUser({ ...user, kyc_status: r.data.status, kyc_tier: r.data.tier });
      setProcessing(false);
      onComplete?.();
    }, 1800);
  };

  return (
    <div className="space-y-5" data-testid="kyc-screen">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-full bg-primary/5 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-primary">Quick identity check</h2>
          <p className="text-xs text-slate-500">Required by EU regulation · One-time, takes ~2 min</p>
        </div>
      </div>

      <VpnWarning />

      <div className="space-y-2">
        {steps.map((s, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className={`flex items-center gap-3 p-3 rounded-2xl border transition-all
                ${active ? "border-accent bg-accent/5" : done ? "border-accent/30 bg-white" : "border-slate-200 bg-white"}`}
              data-testid={`kyc-step-${i}`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center
                ${done ? "bg-accent text-white" : active ? "bg-primary text-white" : "bg-slate-100 text-slate-400"}`}>
                {done ? <CheckCircle2 className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <div className="font-display font-semibold text-sm text-primary">{s.title}</div>
                <div className="text-xs text-slate-500">{s.desc}</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600 flex gap-2">
        <ShieldCheck className="w-4 h-4 text-accent shrink-0 mt-0.5" />
        <div>
          Sumsub-powered identity verification. Documents are encrypted and never stored on Xchangely servers.
        </div>
      </div>

      <Button
        onClick={run} disabled={processing}
        className="w-full bg-accent hover:bg-accent/90 text-white rounded-xl h-12 font-display font-semibold"
        data-testid="kyc-start-btn"
      >
        {processing ? "Verifying…" : "Start verification"}
      </Button>
    </div>
  );
}
